import React from 'react';
import { motion } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import Card from '../ui/Card';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiCalendar, FiFileText, FiAlertCircle, FiCheckCircle } = FiIcons;

const MembershipStatus = ({ membershipDetails }) => {
  if (!membershipDetails) return null;
  
  const { plan, applicationsUsed, applicationsLimit, startDate, endDate, remainingApplications } = membershipDetails;
  
  const startDateFormatted = format(new Date(startDate), 'MMM dd, yyyy');
  const endDateFormatted = format(new Date(endDate), 'MMM dd, yyyy');
  const timeRemaining = formatDistanceToNow(new Date(endDate), { addSuffix: true });
  
  // Calculate usage percentage for progress bar
  const usagePercentage = applicationsLimit === null 
    ? 0 
    : Math.min(100, Math.round((applicationsUsed / applicationsLimit) * 100));
  
  // Determine if membership is almost expired (within 7 days)
  const isAlmostExpired = () => {
    const now = new Date();
    const end = new Date(endDate);
    const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return daysLeft <= 7;
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Your Active Membership</h3>
        <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
          Active
        </span>
      </div>
      
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-4 rounded-lg mb-6">
        <div className="flex justify-between items-center">
          <h4 className="text-xl font-bold text-gray-900">{plan.name}</h4>
          <span className="text-primary-600 font-medium">₹{plan.price}</span>
        </div>
      </div>
      
      <div className="space-y-4 mb-6">
        <div>
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700">Subscription Period</span>
          </div>
          <p className="text-sm ml-6">
            {startDateFormatted} - {endDateFormatted}
          </p>
        </div>
        
        <div>
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiClock} className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700">Time Remaining</span>
          </div>
          <p className={`text-sm ml-6 ${isAlmostExpired() ? 'text-red-600 font-medium' : ''}`}>
            Expires {timeRemaining}
            {isAlmostExpired() && (
              <span className="flex items-center mt-1">
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500 mr-1" />
                Your membership is expiring soon!
              </span>
            )}
          </p>
        </div>
        
        <div>
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiFileText} className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700">Applications</span>
          </div>
          
          {applicationsLimit === null ? (
            <div className="flex items-center ml-6">
              <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-sm">Unlimited applications</span>
            </div>
          ) : (
            <div className="ml-6">
              <div className="flex justify-between text-xs mb-1">
                <span>{applicationsUsed} used</span>
                <span>{applicationsLimit} total</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${usagePercentage}%` }}
                  transition={{ duration: 0.5 }}
                  className={`h-2.5 rounded-full ${
                    usagePercentage > 75 ? 'bg-red-600' : 
                    usagePercentage > 50 ? 'bg-yellow-500' : 
                    'bg-green-600'
                  }`}
                />
              </div>
              <p className="text-sm mt-1">
                {remainingApplications} application{remainingApplications !== 1 ? 's' : ''} remaining
              </p>
            </div>
          )}
        </div>
      </div>
      
      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800">
          With your membership, you only pay the application fees, no service charges apply.
        </p>
      </div>
    </Card>
  );
};

export default MembershipStatus;